import { useState, useCallback } from 'react';
import { Page } from "@jobber/components/Page";
import { Card, Button, Content, Grid, showToast, InputPassword } from '@jobber/components';
import { useNavigate } from 'react-router-dom';
import { api } from "@/services/api";
import { useUserContext } from "@/contexts";

const ImpersonatePage = () => {
  const [adminToken, setAdminToken] = useState('0ac266a6d543f3d3d34500c4274b62e1afffadd84e303fff8296c2d925bce011');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [accounts, setAccounts] = useState<Array<{ accountName: string, id: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useUserContext();

  const handleAdminAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api('/admin-get-accounts', {
        headers: {
          'x-auth-token': adminToken
        }
      });

      if (response.status !== 200) {
        throw new Error('Invalid admin token');
      }

      setAccounts(response.data);
      setIsAuthorized(true);
      showToast({
        message: 'Admin authenticated successfully',
        variation: 'success',
      });
    } catch (error) {
      showToast({
        message: 'Failed to authenticate admin token',
        variation: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [adminToken]);

  const handleImpersonate = useCallback(async (accountId: string) => {
    try {
      setIsLoading(true);
      const response = await api('/admin-impersonate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': adminToken
        },
        data: { accountId }
      });

      if (response.status !== 200) {
        throw new Error('Failed to impersonate account');
      }

      setUser(response.data);

      showToast({
        message: 'Successfully impersonating account',
        variation: 'success',
      });
      navigate('/home');
    } catch (error) {
      showToast({
        message: 'Failed to impersonate account',
        variation: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [adminToken, navigate, setUser]);

  return (
    <Page title="Admin Impersonation">
      <Content type="div">
        <Grid>
          <Grid.Cell size={{ xs: 12, sm: 12, md: 8 }}>
            <Card>
              <Content>
                {!isAuthorized ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <InputPassword
                      name="AdminToken"
                      placeholder="Enter admin token"
                      value={adminToken}
                      onChange={(value) => setAdminToken(value as string)}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        label="Authenticate"
                        onClick={handleAdminAuth}
                        loading={isLoading}
                        disabled={!adminToken || isLoading}
                      />
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {accounts.map(account => (
                      <Button
                        key={account.id}
                        label={account.accountName}
                        onClick={() => handleImpersonate(account.id)}
                        loading={isLoading}
                        variation="subtle"
                      />
                    ))}
                  </div>
                )}
              </Content>
            </Card>
          </Grid.Cell>
        </Grid>
      </Content>
    </Page>
  );
};

export default ImpersonatePage;